import { create } from "zustand";

/**
 * Audio State Store
 * Centralized state management for audio system, video playback, and BGM volume
 */
const useAudioStore = create((set, get) => {
	return {
		// Active BGM system: 'web', 'html5', or null
		activeBgmSystem: null,

		// Video playback state
		isVideoPlaying: false,
		currentVideoRef: null, // Ref to the currently playing video element

		// BGM volume state
		currentBgmVolume: 0.2, // Default full BGM volume // Current BGM volume
		previousBgmVolume: null, // Volume before ducking (for restoration)
		isBgmDucked: false, // Whether BGM is currently ducked
		duckVolume: 0.03, // Volume to duck to (default 0.03)

		// Actions
		setActiveBgmSystem: (system) => {
			set({ activeBgmSystem: system });
		},

		setVideoPlaying: (isPlaying, videoRef = null) => {
			set({
				isVideoPlaying: isPlaying,
				currentVideoRef: videoRef,
			});
		},

		setBgmVolume: (volume) => {
			set({ currentBgmVolume: volume });
		},

		setPreviousBgmVolume: (volume) => {
			set({ previousBgmVolume: volume });
		},

		setBgmDucked: (isDucked) => {
			set({ isBgmDucked: isDucked });
		},

		// Duck BGM - saves current volume and sets ducked state
		duckBgm: (duckVolume = 0.03) => {
			const state = get();
			if (!state.isBgmDucked && state.previousBgmVolume === null) {
				set({
					previousBgmVolume: state.currentBgmVolume,
					isBgmDucked: true,
					duckVolume,
				});
			} else if (!state.isBgmDucked) {
				// Already have previous volume, just mark as ducked
				set({
					isBgmDucked: true,
					duckVolume,
				});
			}
		},

		// Restore BGM - restores to previous volume and clears ducked state
		restoreBgm: () => {
			const state = get();
			if (state.isBgmDucked && state.previousBgmVolume !== null) {
				set({
					currentBgmVolume: state.previousBgmVolume,
					isBgmDucked: false,
					previousBgmVolume: null,
				});
			} else if (state.isBgmDucked) {
				// No previous volume saved, restore to default
				set({
					currentBgmVolume: 0.2, // Default full BGM volume
					isBgmDucked: false,
					previousBgmVolume: null,
				});
			}
		},

		// Get the volume that should be used when unmuting
		// If video is playing and BGM was ducked, return ducked volume
		// Otherwise return the saved previous volume or current volume
		getUnmuteVolume: () => {
			const state = get();
			if (state.isVideoPlaying && state.isBgmDucked) {
				return state.duckVolume; // Return ducked volume if video is playing
			}
			if (state.previousBgmVolume !== null) {
				return state.previousBgmVolume; // Return saved volume
			}
			return state.currentBgmVolume; // Return current volume
		},

		// Reset all state (for cleanup)
		reset: () => {
			set({
				activeBgmSystem: null,
				isVideoPlaying: false,
				currentVideoRef: null,
				currentBgmVolume: 0.2, // Default full BGM volume
				previousBgmVolume: null,
				isBgmDucked: false,
				duckVolume: 0.03,
			});
		},
	};
});

export default useAudioStore;
